import type { FC } from "react";
import { GBFlagIcon, USFlagIcon } from "@/components/ui/icons/FlagIcons";
import { CheckIcon } from "@/components/ui/icons/ThemeIcons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/primitives/Dropdown";
import type { SiteVariant } from "@/context/VariantContext";
import { useVariant } from "@/context/VariantContext";
import { transitions } from "@/styles/transitions";
import { cn } from "@/utils/cn";
import { themeComponentStyles } from "@/utils/themeUtils";
import { setVariantAndReload } from "@/utils/variant";

/**
 * VariantSelect
 *
 * A dropdown menu for selecting between UK and US site variants.
 * Optimized for mobile use with compact design.
 * Uses country flags for visual representation.
 */
export const VariantSelect: FC = () => {
  const { variant } = useVariant();

  // Variant options with their associated flags
  const variants = [
    { FlagIcon: USFlagIcon, id: "us" as SiteVariant, label: "United States" },
    { FlagIcon: GBFlagIcon, id: "uk" as SiteVariant, label: "United Kingdom" },
  ];

  // Get current variant's flag component
  const CurrentFlag =
    variants.find((v) => v.id === variant)?.FlagIcon ?? USFlagIcon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Select site variant"
        className={cn(
          "flex items-center gap-2 text-sm px-2.5 cursor-pointer flex-shrink-0",
          transitions.colors,
          themeComponentStyles.getContainerClasses(),
          "hover:bg-theme-muted/70",
          themeComponentStyles.getControlClasses(),
        )}
      >
        {/* Current variant flag */}
        <CurrentFlag className="h-4 w-4" />
        <svg
          aria-hidden="true"
          className="h-4 w-4 transition-transform-standard hidden sm:block"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <title>Toggle dropdown</title>
          <path
            d="M19 9l-7 7-7-7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[12rem] p-1"
        sideOffset={4}
      >
        <DropdownMenuLabel>Region</DropdownMenuLabel>

        {variants.map((variantOption) => {
          const isActive = variant === variantOption.id;
          const FlagIcon = variantOption.FlagIcon;

          return (
            <DropdownMenuItem
              className={cn(
                "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
                transitions.colors,
                isActive
                  ? "bg-theme-muted/50 text-theme-primary"
                  : "hover:bg-theme-muted/70 text-theme-muted-foreground",
              )}
              key={variantOption.id}
              onSelect={() => {
                if (variantOption.id !== variant) {
                  setVariantAndReload(variantOption.id);
                }
              }}
            >
              <FlagIcon className="h-4 w-4" />
              <span className="font-medium">{variantOption.label}</span>
              {isActive && (
                <CheckIcon className="h-4 w-4 ml-auto text-theme-primary" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
